import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, withModifiers as _withModifiers, resolveComponent as _resolveComponent, createVNode as _createVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "container-fluid admin-page" }
const _hoisted_2 = { class: "title-area" }
const _hoisted_3 = { class: "csv-btn-wrapper" }
const _hoisted_4 = {
  class: "btn btn-secondary mb0 ml8",
  for: "csvFileInput"
}
const _hoisted_5 = {
  key: 0,
  class: "fa fa-upload"
}
const _hoisted_6 = {
  key: 1,
  class: "fa fa-spinner fa-spin"
}
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = {
  key: 0,
  class: "fa fa-download"
}
const _hoisted_10 = {
  key: 1,
  class: "fa fa-spinner fa-spin"
}
const _hoisted_11 = { class: "search-area" }
const _hoisted_12 = { class: "form-group" }
const _hoisted_13 = ["value"]
const _hoisted_14 = { class: "form-group" }
const _hoisted_15 = ["value"]
const _hoisted_16 = { class: "form-group" }
const _hoisted_17 = { class: "form-group" }
const _hoisted_18 = ["value"]
const _hoisted_19 = { class: "p1" }

import userApi from 'src/apis/user';
import group2Api from 'src/apis/group2';
import group3Api from 'src/apis/group3';
import { computed, onMounted, reactive, type ComputedRef } from 'vue';
import type { Lov } from 'src/models/master';
import type { AdminGroup2 } from 'src/models/group2';
import type { AdminGroup3 } from 'src/models/group3';
import type { AdminUser, AdminUserCsvResult, AdminUserSearchParams } from 'src/models/user';
import type {
  AdminUserCreateParams,
  AdminUserPasswordUpdateParams,
  AdminUserUpdateParams,
} from 'src/models/apis/user/adminUserRequest';
import { useAuth } from 'src/composables/useAuth';
import { useReactiveMasters } from 'src/composables/useMasters';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import { downloadBlob } from 'src/lib/downloadHelper';
import { dtFormat } from 'src/lib/dateHelper';
import { lineBreakToBR } from 'src/lib/utils';
import type { AxiosError } from 'axios';
import type { AdminListTableField, CustomButton } from 'src/models/adminListTable';
import UserEditModal from 'src/components/Admin/User/UserEditModal.vue';
import UserCsvUploadModal from 'src/components/Admin/User/UserCsvUploadModal.vue';

type UsersDisplayRow = AdminUser & { gName: string; roleDisp: string | number };

interface UsersSearchState {
  roles: Lov[];
  groups2: AdminGroup2[];
  groups3: AdminGroup3[];
  search: AdminUserSearchParams;
}

interface UsersEditState {
  showEditModal: boolean;
  editMode: 'create' | 'edit' | 'editPassword';
  editingUser: AdminUser | null;
  httpErrorMsgs: string[];
  showDestroyModal: boolean;
  destroyingUser: AdminUser | null;
  showErrorModal: boolean;
}

interface UsersCsvState {
  uploadingCsv: File | null;
  csvResult: AdminUserCsvResult | null;
  csvErrorBody: string;
  isDownloading: boolean;
  isUploading: boolean;
  showConfirmCsvUploadModal: boolean;
  showCsvUploadCompleteModal: boolean;
  showCsvErrorModal: boolean;
}

const destroyModalTitle = '削除';
const destroyModalMsg = 'を削除してもよろしいですか？';
const errorModalTitle = 'エラー';
const errorModalMsg = '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。';

export default /*@__PURE__*/_defineComponent({
  __name: 'Users',
  setup(__props) {

const initSearchState = (): AdminUserSearchParams => {
  return {
    group2Id: null,
    group3Id: null,
    userIdOrUserName: null,
    role: null,
  };
};

const searchState = reactive<UsersSearchState>({
  roles: [],
  groups2: [],
  groups3: [],
  search: initSearchState(),
});
const filteredUsers = reactive<{ list: AdminUser[] }>({ list: [] });
const editState = reactive<UsersEditState>({
  showEditModal: false,
  editMode: 'edit',
  editingUser: null,
  httpErrorMsgs: [],
  showDestroyModal: false,
  destroyingUser: null,
  showErrorModal: false,
});
const csvState = reactive<UsersCsvState>({
  uploadingCsv: null,
  csvResult: null,
  csvErrorBody: '',
  isDownloading: false,
  isUploading: false,
  showConfirmCsvUploadModal: false,
  showCsvUploadCompleteModal: false,
  showCsvErrorModal: false,
});

const auth = useAuth();
const { masters, resolveMastersPromise } = useReactiveMasters();

const selectableSearchGroup3 = computed<AdminGroup3[]>(() => {
  if (searchState.search.group2Id == null) return searchState.groups3;

  return searchState.groups3.filter((e) => {
    return e.parentId === searchState.search.group2Id;
  });
});
const selectableRoles = computed<Lov[]>(() => {
  const loginUser = auth.user;
  if (!loginUser) return [];

  // 自分と同じか自分より弱い権限しか選択できない.
  return searchState.roles.filter((e) => parseInt(e.key) >= loginUser.role);
});
const roleLabelMap = computed<Map<number, string>>(() => {
  return new Map(searchState.roles.map((e) => [parseInt(e.key), e.val]));
});
const isLoginUser = (otherUser: { id: number } | null): boolean => {
  if (!auth.user || !otherUser) return false;

  return auth.user.id === otherUser.id;
};
const canEditUser = (otherUser: AdminUser): boolean => {
  if (!otherUser || !otherUser.groupInfo) return false;

  const loginUser = auth.user;
  if (!loginUser) return false;

  const otherUserRole = Number(otherUser.role);
  if (loginUser.role > otherUserRole) return false;

  if (loginUser.hasRoleOperationsAdmin) {
    return loginUser.groupInfo.g1id === otherUser.groupInfo.g1id;
  } else if (loginUser.hasRoleBranchAdmin) {
    return loginUser.groupInfo.g2id === otherUser.groupInfo.g2id;
  } else if (loginUser.hasRoleDepartmentAdmin) {
    return loginUser.groupInfo.g3id === otherUser.groupInfo.g3id;
  }
  return false;
};
const canDeleteUser = (otherUser: AdminUser): boolean => {
  return canEditUser(otherUser) && !isLoginUser(otherUser);
};
const displayData: ComputedRef<UsersDisplayRow[]> = computed(() => {
  return filteredUsers.list.map(user => {
    const gName = `${user.groupInfo.g2name}\n${user.groupInfo.g3name}`;
    return {
      ...user,
      gName: lineBreakToBR(gName),
      roleDisp: roleLabelMap.value.get(user.role) ?? user.role,
    };
  });
});

const filterSearch = (usersOrig: AdminUser[]): void => {
  filteredUsers.list = usersOrig.filter(user => {
    if (searchState.search.group2Id != null &&
      user.groupInfo.g2id !== searchState.search.group2Id) {
      return false;
    }
    if (searchState.search.group3Id != null &&
      user.groupInfo.g3id !== searchState.search.group3Id) {
      return false;
    }
    if (searchState.search.userIdOrUserName != null &&
      searchState.search.userIdOrUserName !== '' &&
      user.username.indexOf(searchState.search.userIdOrUserName) < 0 &&
      user.displayName.indexOf(searchState.search.userIdOrUserName) < 0) {
      return false;
    }
    if (searchState.search.role != null &&
      searchState.search.role !== '' &&
      user.role !== parseInt(searchState.search.role)) {
      return false;
    }
    return true;
  });
};
const doSearch = async(): Promise<void> => {
  filteredUsers.list = [];
  const users = await userApi.adminIndex({});
  if (!users || users.length === 0) return;

  filterSearch(users);
};

const fields: AdminListTableField<UsersDisplayRow>[] = [
  { name: 'gName', label: '所属組織', type: 'html', class: 'ws-nowrap' },
  { name: 'username', label: 'ユーザーID' },
  { name: 'displayName', label: 'ユーザー名' },
  { name: 'roleDisp', label: '権限', class: 'ws-nowrap' },
];

const create = (): void => {
  editState.editMode = 'create';
  editState.editingUser = null;
  editState.httpErrorMsgs = [];
  editState.showEditModal = true;
};
const edit = (obj: AdminUser): void => {
  editState.editMode = 'edit';
  editState.editingUser = obj;
  editState.httpErrorMsgs = [];
  editState.showEditModal = true;
};
const editPassword = (obj: AdminUser): void => {
  editState.editMode = 'editPassword';
  editState.editingUser = obj;
  editState.httpErrorMsgs = [];
  editState.showEditModal = true;
};
const tryDestroy = (obj: AdminUser): void => {
  if (!canDeleteUser(obj)) return;

  editState.destroyingUser = obj;
  editState.showDestroyModal = true;
};
const doDestroy = async(): Promise<void> => {
  if (!editState.destroyingUser) return;

  try {
    await userApi.adminDestroy(editState.destroyingUser.id);
    editState.showDestroyModal = false;
    void doSearch();
  } catch {
    editState.showDestroyModal = false;
    editState.showErrorModal = true;
  }
};

const runSave = async(apiCall: () => Promise<unknown>): Promise<void> => {
  try {
    await apiCall();
    editState.httpErrorMsgs = [];
    editState.showEditModal = false;
    void doSearch();
  } catch (err) {
    editState.httpErrorMsgs = getErrorMessages(err as AxiosError);
  }
};
const onCreateUser = (payload: AdminUserCreateParams): Promise<void> => {
  return runSave(() => userApi.adminCreate(payload));
};
const onUpdateProfile = (id: number, payload: AdminUserUpdateParams): Promise<void> => {
  return runSave(() => userApi.adminUpdate(id, payload));
};
const onUpdatePassword = (id: number, payload: AdminUserPasswordUpdateParams): Promise<void> => {
  return runSave(() => userApi.adminPasswordUpdate(id, payload));
};

const customButtons: CustomButton<AdminUser>[] = [
  {
    label: 'パスワード変更',
    clickHandler: (obj: AdminUser) => {
      editPassword(obj);
    },
    isVisible: () => true,
    isDisabled: (obj: AdminUser) => {
      return !canEditUser(obj);
    },
  },
];

const openConfirmCsvUploadModal = (evt: Event): void => {
  const eventTarget = evt.target as HTMLInputElement;
  if (!eventTarget || !eventTarget.files) return;

  const file = eventTarget.files[0];
  if (!file) return;

  if (!file.name.toLowerCase().endsWith('.csv')) {
    eventTarget.value = '';
    csvState.csvErrorBody = 'CSVファイル（拡張子.csv）を選択してください';
    csvState.showCsvErrorModal = true;
    return;
  }

  csvState.uploadingCsv = file;
  csvState.showConfirmCsvUploadModal = true;
};
const downloadCSV = async(): Promise<void> => {
  csvState.isDownloading = true;
  try {
    // NOTE: 送信キーは従来挙動を維持する（user_id_or_user_name のまま）.
    const searchParams = {
      group2_id: searchState.search.group2Id,
      group3_id: searchState.search.group3Id,
      user_id_or_user_name: searchState.search.userIdOrUserName,
      role: searchState.search.role,
    };
    const { data: csvBlob } = await userApi.adminDownloadCSV(searchParams);
    const timestamp = dtFormat(new Date(), 'yyyymmdd_HHMMSS');
    const filename = `ユーザー一覧_${timestamp}`;
    downloadBlob(csvBlob, filename);
    csvState.isDownloading = false;
  } catch {
    csvState.csvErrorBody = 'CSVダウンロードに失敗しました。再度操作を行ってください';
    csvState.showCsvErrorModal = true;
    csvState.isDownloading = false;
  }
};
const closeConfirmCsvUploadModal = (): void => {
  csvState.uploadingCsv = null;
  csvState.showConfirmCsvUploadModal = false;
};
const closeCsvUploadCompleteModal = (): void => {
  csvState.csvResult = null;
  csvState.showCsvUploadCompleteModal = false;
};
const uploadCSV = async(): Promise<void> => {
  if (!csvState.uploadingCsv) return;

  csvState.isUploading = true;
  try {
    const formData = new FormData();
    formData.append('file', csvState.uploadingCsv, csvState.uploadingCsv.name);
    const csvResult = await userApi.adminUploadCSV(formData);
    csvState.csvResult = {
      ...csvResult,
      hasErrors: csvResult.failures.length > 0,
    };
    csvState.showCsvUploadCompleteModal = true;
    closeConfirmCsvUploadModal();
    csvState.isUploading = false;
    void doSearch();
  } catch {
    csvState.csvErrorBody = 'CSVアップロードに失敗しました。再度操作を行ってください';
    csvState.showCsvErrorModal = true;
    closeConfirmCsvUploadModal();
    csvState.isUploading = false;
  }
};

onMounted(async() => {
  const [groups2, groups3] = await Promise.all([
    group2Api.adminIndex(),
    group3Api.adminIndex(),
  ]);
  searchState.groups2 = groups2;
  searchState.groups3 = groups3;
  void doSearch();
  await resolveMastersPromise();
  searchState.roles = masters.lov.roles;
});


return (_ctx: any,_cache: any) => {
  const _component_AdminListTable = _resolveComponent("AdminListTable")!
  const _component_Modal = _resolveComponent("Modal")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[13] || (_cache[13] = _createElementVNode("h2", null, "ユーザー管理", -1)),
      _createElementVNode("div", { class: "create-btn" }, [
        _createElementVNode("button", {
          class: "btn btn-info",
          onClick: create
        }, [...(_cache[10] || (_cache[10] = [
          _createElementVNode("i", { class: "fas fa-plus" }, null, -1),
          _createElementVNode("span", { class: "ml-1" }, "新規作成", -1)
        ]))])
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("label", _hoisted_4, [
          (!csvState.isUploading)
            ? (_openBlock(), _createElementBlock("i", _hoisted_5))
            : (_openBlock(), _createElementBlock("i", _hoisted_6)),
          _cache[11] || (_cache[11] = _createTextVNode(" 一括 ", -1))
        ]),
        _createElementVNode("input", {
          type: "file",
          id: "csvFileInput",
          style: {"display":"none"},
          accept: ".csv",
          onClick: _cache[0] || (_cache[0] = ($event: any) => ($event.target.value = null)),
          onChange: openConfirmCsvUploadModal,
          disabled: csvState.isUploading
        }, null, 40, _hoisted_7),
        _createElementVNode("button", {
          class: "btn btn-secondary ml8",
          onClick: downloadCSV,
          disabled: csvState.isDownloading
        }, [
          (!csvState.isDownloading)
            ? (_openBlock(), _createElementBlock("i", _hoisted_9))
            : (_openBlock(), _createElementBlock("i", _hoisted_10)),
          _cache[12] || (_cache[12] = _createTextVNode(" 一括 ", -1))
        ], 8, _hoisted_8)
      ])
    ]),
    _createElementVNode("div", _hoisted_11, [
      _createElementVNode("form", {
        class: "search-form",
        onSubmit: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["prevent"]))
      }, [
        _createElementVNode("div", _hoisted_12, [
          _cache[15] || (_cache[15] = _createElementVNode("label", { class: "form-control-label" }, "事業所", -1)),
          _withDirectives(_createElementVNode("select", {
            class: "form-control",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchState.search.group2Id) = $event))
          }, [
            _cache[14] || (_cache[14] = _createElementVNode("option", { value: null }, null, -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(searchState.groups2, (e) => {
              return (_openBlock(), _createElementBlock("option", {
                key: e.id,
                value: e.id
              }, _toDisplayString(e.name), 9, _hoisted_13))
            }), 128))
          ], 512), [
            [_vModelSelect, searchState.search.group2Id]
          ])
        ]),
        _createElementVNode("div", _hoisted_14, [
          _cache[17] || (_cache[17] = _createElementVNode("label", { class: "form-control-label" }, "部署", -1)),
          _withDirectives(_createElementVNode("select", {
            class: "form-control",
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((searchState.search.group3Id) = $event))
          }, [
            _cache[16] || (_cache[16] = _createElementVNode("option", { value: null }, null, -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectableSearchGroup3.value, (e) => {
              return (_openBlock(), _createElementBlock("option", {
                key: e.id,
                value: e.id
              }, _toDisplayString(e.name), 9, _hoisted_15))
            }), 128))
          ], 512), [
            [_vModelSelect, searchState.search.group3Id]
          ])
        ]),
        _createElementVNode("div", _hoisted_16, [
          _cache[18] || (_cache[18] = _createElementVNode("label", { class: "form-control-label" }, "ユーザーID・ユーザー名", -1)),
          _withDirectives(_createElementVNode("input", {
            type: "text",
            class: "form-control",
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((searchState.search.userIdOrUserName) = $event))
          }, null, 512), [
            [_vModelText, searchState.search.userIdOrUserName]
          ])
        ]),
        _createElementVNode("div", _hoisted_17, [
          _cache[20] || (_cache[20] = _createElementVNode("label", { class: "form-control-label" }, "権限", -1)),
          _withDirectives(_createElementVNode("select", {
            class: "form-control",
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((searchState.search.role) = $event))
          }, [
            _cache[19] || (_cache[19] = _createElementVNode("option", { value: null }, null, -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectableRoles.value, (e) => {
              return (_openBlock(), _createElementBlock("option", {
                key: e.key,
                value: e.key
              }, _toDisplayString(e.val), 9, _hoisted_18))
            }), 128))
          ], 512), [
            [_vModelSelect, searchState.search.role]
          ])
        ]),
        _createElementVNode("div", { class: "form-group search-btn ta-center" }, [
          _createElementVNode("button", {
            type: "submit",
            class: "btn btn btn-primary",
            onClick: doSearch
          }, " 検索 ")
        ])
      ], 32)
    ]),
    _createVNode(_component_AdminListTable, {
      "display-data": displayData.value,
      fields: fields,
      "custom-buttons": customButtons,
      "can-delete": canDeleteUser,
      "can-edit": canEditUser,
      "always-show-buttons": true,
      onStartEdit: edit,
      onStartDestroy: tryDestroy
    }, null, 8, ["display-data"]),
    (editState.showEditModal)
      ? (_openBlock(), _createBlock(UserEditModal, {
          key: 0,
          "edit-mode": editState.editMode,
          "editing-user": editState.editingUser,
          groups2: searchState.groups2,
          groups3: searchState.groups3,
          "selectable-roles": selectableRoles.value,
          "http-error-msgs": editState.httpErrorMsgs,
          onCreate: onCreateUser,
          onUpdateProfile: onUpdateProfile,
          onUpdatePassword: onUpdatePassword,
          onClose: _cache[6] || (_cache[6] = ($event: any) => (editState.showEditModal = false))
        }, null, 8, ["edit-mode", "editing-user", "groups2", "groups3", "selectable-roles", "http-error-msgs"]))
      : _createCommentVNode("", true),
    (editState.showDestroyModal)
      ? (_openBlock(), _createBlock(_component_Modal, {
          key: 1,
          title: destroyModalTitle,
          "show-bottom-dismiss-button": true,
          onClose: doDestroy,
          onDismiss: _cache[7] || (_cache[7] = ($event: any) => (editState.showDestroyModal = false))
        }, {
          body: _withCtx(() => [
            _createElementVNode("div", null, [
              _createElementVNode("p", _hoisted_19, [
                _cache[21] || (_cache[21] = _createTextVNode("ユーザーID", -1)),
                _cache[22] || (_cache[22] = _createElementVNode("span", { class: "colon" }, null, -1)),
                _createTextVNode(_toDisplayString(editState.destroyingUser?.username), 1)
              ]),
              _createElementVNode("p", {
                class: "p2 font-mtx-desc",
                innerHTML: destroyModalMsg
              })
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (editState.showErrorModal)
      ? (_openBlock(), _createBlock(_component_Modal, {
          key: 2,
          title: errorModalTitle,
          "show-bottom-dismiss-button": false,
          onClose: _cache[8] || (_cache[8] = ($event: any) => (editState.showErrorModal = false))
        }, {
          body: _withCtx(() => [
            _createElementVNode("div", null, [
              _createElementVNode("p", {
                class: "p2 font-mtx-desc",
                innerHTML: errorModalMsg
              })
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(UserCsvUploadModal, {
      "show-confirm": csvState.showConfirmCsvUploadModal,
      "show-complete": csvState.showCsvUploadCompleteModal,
      "show-error": csvState.showCsvErrorModal,
      "uploading-csv": csvState.uploadingCsv,
      "csv-result": csvState.csvResult,
      "csv-error-body": csvState.csvErrorBody,
      "is-uploading": csvState.isUploading,
      onConfirm: uploadCSV,
      onDismissConfirm: closeConfirmCsvUploadModal,
      onDismissComplete: closeCsvUploadCompleteModal,
      onDismissError: _cache[9] || (_cache[9] = ($event: any) => (csvState.showCsvErrorModal = false))
    }, null, 8, ["show-confirm", "show-complete", "show-error", "uploading-csv", "csv-result", "csv-error-body", "is-uploading"])
  ]))
}
}

})